"use client";

import { type FC, Suspense } from "react";

import { isEditing } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { Container } from "@jti/core-components/components/Container";
import { buildShareProviders } from "@jti/ui/utils/share";
import type { AppModel } from "@jti/user-profile";

import { useLanguages } from "@/hooks/useLanguages";
import { useTransformImageUrl } from "@/hooks/useTransformImageUrl";
import { mapAgeVerificationMethodImages, mapDeviceCodeLengths } from "@/libs/builder/common/reactAppsAuthoringMapping";

import { ErrorBoundary } from "../ErrorBoundary/ErrorBoundary";
import { Placeholder } from "../Placeholder/Placeholder";

import type { userProfileInputs } from "./UserProfile.builder";

export type UserProfileProps = BuilderComponentProps<typeof userProfileInputs>;

const App = dynamic(async () => (await import("@jti/user-profile")).UserProfile, {
  ssr: false,
});

const AppModelProvider = dynamic(async () => (await import("@jti/user-profile")).AppModelProvider, { ssr: false });

const LanguagesProvider = dynamic(async () => (await import("@jti/user-profile")).LanguagesProvider, { ssr: false });

export const UserProfile: FC<UserProfileProps> = ({
  payOnDelivery,
  zeroValueOrder,
  headerBanner,
  failedPaymentFlow,
  communicationPreferences,
  orderList,
  deviceManagement,
  genderInformation,
  personalInformation,
  sharableProfile,
  passPersonalDataUpdate,
  subscriptionConfiguration,
  referredByFriendConfiguration,
  referralPersonalCodeConfiguration,
  ageVerificationMethodImages,
  deleteAccount,
  isLoyaltyReferralCodeExtendedEnabled,
  orderBannerConfiguration,
}) => {
  const languages = useLanguages();
  const transformImageUrl = useTransformImageUrl();

  const deviceCodeLengths = mapDeviceCodeLengths(deviceManagement?.deviceCodeLength);

  const shareConfig = referralPersonalCodeConfiguration?.shareConfig
    ? {
        ...referralPersonalCodeConfiguration?.shareConfig,
        providers: buildShareProviders(referralPersonalCodeConfiguration?.shareConfig?.providers),
      }
    : undefined;

  const userProfileAppModel: AppModel = {
    ageVerificationMethodImages: mapAgeVerificationMethodImages(ageVerificationMethodImages),
    deviceManagement: {
      ...deviceManagement,
      deviceCodeLength: deviceCodeLengths,
      isLastDeviceRemovalRestricted: !!deviceManagement?.isLastDeviceRemovalRestricted,
      successfulDeviceRegistrationCTA: deviceManagement?.successfulDeviceRegistrationCTA || "",
      isVideoTutorialSectionHidden: !!deviceManagement?.isVideoTutorialSectionHidden,
    },
    failedPaymentFlow: {
      ...failedPaymentFlow,
      title: failedPaymentFlow?.title || "",
      message: failedPaymentFlow?.message || "",
      submitButtonLabel: failedPaymentFlow?.submitButtonLabel || "",
    },
    genderInformation: {
      isGenderEnabled: !!genderInformation?.isGenderEnabled,
    },
    headerBanner: {
      ...headerBanner,
      hideGreetingMessage: !!headerBanner?.hideGreetingMessage,
    },
    orderList: {
      ...orderList,
      isOfflineOrderEnabled: !!orderList?.isOfflineOrderEnabled,
    },
    payOnDelivery: {
      ...payOnDelivery,
      paymentMethod: payOnDelivery?.paymentMethod || "",
    },
    personalInformation: {
      dobChangeRestrictedMessage: personalInformation?.dobChangeRestrictedMessage || "",
      isDobChangeRestricted: !!personalInformation?.isDobChangeRestricted,
      hideMyAddress: !!personalInformation?.hideMyAddress,
    },
    sharableProfile: {
      isSharableProfileLoyaltyEnabled: !!sharableProfile?.isSharableProfileLoyaltyEnabled,
      isSharableProfileEnabled: !!sharableProfile?.isSharableProfileEnabled,
      clubCardLogo: sharableProfile?.clubCardLogo || "",
    },
    passPersonalDataUpdate: {
      ignoreUrls: [],
      ...passPersonalDataUpdate,
      title: passPersonalDataUpdate?.title || "",
      isPassPersonalDataUpdate: !!passPersonalDataUpdate?.isPassPersonalDataUpdate,
    },
    referredByFriendConfiguration: {
      ...referredByFriendConfiguration,
      discountImageDesktop: referredByFriendConfiguration?.discountImageDesktop || "",
      discountImageTablet: referredByFriendConfiguration?.discountImageTablet || "",
      discountImageMobile: referredByFriendConfiguration?.discountImageMobile || "",
      successImageDesktop: referredByFriendConfiguration?.successImageDesktop || "",
      successImageMobile: referredByFriendConfiguration?.successImageMobile || "",
      successHeading: referredByFriendConfiguration?.successHeading || "",
      successLabel: referredByFriendConfiguration?.successLabel || "",
      successBtnLabel: referredByFriendConfiguration?.successBtnLabel || "",
      successLink: referredByFriendConfiguration?.successLink || "",
      heading: referredByFriendConfiguration?.heading || "",
      label: referredByFriendConfiguration?.label || "",
      list: referredByFriendConfiguration?.list || [],
      legalText: referredByFriendConfiguration?.legalText || "",
      discountHeading: referredByFriendConfiguration?.discountHeading || "",
      discountLabel: referredByFriendConfiguration?.discountLabel || "",
      discountSubText: referredByFriendConfiguration?.discountSubText || "",
      validationRequiredMessage: referredByFriendConfiguration?.validationRequiredMessage || "",
      verifyErrorMessage: referredByFriendConfiguration?.verifyErrorMessage || "",
      inputLabel: referredByFriendConfiguration?.inputLabel || "",
      inputPlaceholder: referredByFriendConfiguration?.inputPlaceholder || "",
      inputHelperText: referredByFriendConfiguration?.inputHelperText || "",
      btnText: referredByFriendConfiguration?.btnText || "",
      isInEditor: isEditing(),
      isHidden: !isLoyaltyReferralCodeExtendedEnabled || !!referredByFriendConfiguration?.isHidden,
    },
    referralPersonalCodeConfiguration: {
      ...referralPersonalCodeConfiguration,
      code: "",
      variant: referralPersonalCodeConfiguration?.variant || "default",
      header: referralPersonalCodeConfiguration?.header || "",
      smallText: referralPersonalCodeConfiguration?.smallText || "",
      backgroundImage: referralPersonalCodeConfiguration?.backgroundImage || "",
      backgroundImageTablet: referralPersonalCodeConfiguration?.backgroundImageTablet || "",
      backgroundImageMobile: referralPersonalCodeConfiguration?.backgroundImageMobile || "",
      referralPageVariant: referralPersonalCodeConfiguration?.referralPageVariant ?? false,
      topHeader: referralPersonalCodeConfiguration?.topHeader || "",
      codeTitle: referralPersonalCodeConfiguration?.codeTitle || "",
      shareTitle: referralPersonalCodeConfiguration?.shareTitle || "",
      description: referralPersonalCodeConfiguration?.description || "",
      items: referralPersonalCodeConfiguration?.items || [],
      withBottomCta: referralPersonalCodeConfiguration?.withBottomCta ?? false,
      bottomCtaLabel: referralPersonalCodeConfiguration?.bottomCtaLabel || "",
      bottomCtaLink: referralPersonalCodeConfiguration?.bottomCtaLink || "",
      isHidden: referralPersonalCodeConfiguration?.isHidden ?? true,
      shareConfig,
    },
    subscriptionConfiguration,
    communicationPreferences: {
      ...communicationPreferences,
      isEmailChannelDisabled: communicationPreferences?.isEmailChannelDisabled || false,
      isPhoneChannelDisabled: communicationPreferences?.isPhoneChannelDisabled || false,
      isPostChannelDisabled: communicationPreferences?.isPostChannelDisabled || false,
      isOptionalConsentNotRequired: communicationPreferences?.isOptionalConsentNotRequired || false,
      oneTrustErrorRedirectUrl: communicationPreferences?.oneTrustErrorRedirectUrl || "",
      emailFieldDescription: communicationPreferences?.emailFieldDescription || "",
      gdprFieldDescription: communicationPreferences?.gdprFieldDescription || "",
      isLanguagePreferenceHidden: !!communicationPreferences?.isLanguagePreferenceHidden,
      oneTrustErrorLinkText: communicationPreferences?.oneTrustErrorLinkText || "",
      oneTrustErrorMessage: communicationPreferences?.oneTrustErrorMessage || "",
      phoneFieldDescription: communicationPreferences?.phoneFieldDescription || "",
      purposesGroupingType: communicationPreferences?.purposesGroupingType || "category",
      postFieldDescription: communicationPreferences?.postFieldDescription || "",
      surveyFieldDescription: communicationPreferences?.surveyFieldDescription || "",
      successUpdateMessage: communicationPreferences?.successUpdateMessage || "",
    },
    zeroValueOrder: {
      ...zeroValueOrder,
      userNotification: zeroValueOrder?.userNotification || "",
    },
    deleteAccount,
    transformImageUrl,
    orderBannerConfiguration,
  };

  if (isEditing()) {
    return <Placeholder>User Profile placeholder</Placeholder>;
  }

  return (
    <ErrorBoundary fallback={null}>
      <Suspense fallback={null}>
        <Container>
          <LanguagesProvider model={languages}>
            <AppModelProvider model={userProfileAppModel}>
              <App />
            </AppModelProvider>
          </LanguagesProvider>
        </Container>
      </Suspense>
    </ErrorBoundary>
  );
};
